import {
  HttpLink,
  Operation,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider as ApolloClientProvider
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/browser';
import { LocalStorageWrapper, CachePersistor } from 'apollo3-cache-persist';
import axios from 'axios';
import { FragmentDefinitionNode, OperationDefinitionNode } from 'graphql';
import { ReactNode } from 'react';
import { GRAPHQL_SERVER_VERSION_URL, GRAPHQL_URL } from '@anytag/shared-constants';
import { getToken, manageLocalStorage } from '@anytag/router';
import { namedOperations } from '@anytag/graphql';

const __DEV__ = process.env.NODE_ENV !== 'production';

const operationInfo = (operation: Operation) => {
  const operationDefinition = operation.query.definitions.find((def) => 'operation' in def) as
    | OperationDefinitionNode
    | undefined;
  const fragmentDefinition = operation.query.definitions.filter(
    (def) => def.kind === 'FragmentDefinition'
  ) as FragmentDefinitionNode[];

  return {
    type: operationDefinition ? operationDefinition.operation : '',
    name: operation.operationName,
    fragments: fragmentDefinition.map((def) => def.name.value).join(', '),
    variables: operation.variables
  };
};

const sentryLink = new ApolloLink((operation, forward) => {
  Sentry.addBreadcrumb({
    category: 'graphql',
    data: operationInfo(operation),
    level: 'debug'
  });

  return forward ? forward(operation) : null;
});
const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${GRAPHQL_URL}?${operation.operationName}`
  }));

  return forward ? forward(operation) : null;
});

const LONG_RUNNING_QUERIES = {
  [namedOperations.Mutation.InfluencerAnalyticsTwitterRefresh]: 300_000,
  [namedOperations.Mutation.RefreshProfileAccount]: 300_000,
  [namedOperations.Mutation.RefreshProfileAudience]: 300_000,
  [namedOperations.Mutation.RefreshTwitterAccount]: 300_000
};
const DEFAULT_TIMEOUT = 60_000;

const getTimeoutForOperation = (operationName: string): number => {
  const timeout = LONG_RUNNING_QUERIES[operationName as keyof typeof LONG_RUNNING_QUERIES] || DEFAULT_TIMEOUT;

  if (__DEV__ && timeout > DEFAULT_TIMEOUT) {
    // eslint-disable-next-line no-console
    console.log(`🕐 Long-running operation detected: ${operationName} (timeout: ${timeout / 1000}s)`);
  }

  return timeout;
};

const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  fetch: async (uri, options) => {
    const controller = new AbortController();

    let operationName = '';
    try {
      if (options?.body && typeof options.body === 'string') {
        const body = JSON.parse(options.body);
        operationName = body.operationName || '';
      }
    } catch {
      // skip
    }

    const timeout = getTimeoutForOperation(operationName);
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      return await fetch(uri, {
        ...options,
        signal: controller.signal
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn(`Request timeout after ${timeout / 1000}s for operation: ${operationName || 'unknown'}`);
      } else {
        // eslint-disable-next-line no-console
        console.error('Network error:', error);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
});
const authLink = setContext((_, { headers }) => {
  const token = getToken();

  return {
    headers: {
      ...headers,
      authorization: token || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  };
});

const showError = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (__DEV__) {
        Sentry.captureException(new Error(message));
        console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      }
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

const cache = new InMemoryCache({
  addTypename: false,
  typePolicies: {
    Query: {
      fields: {
        influencerProfileV2: { merge: true },
        engagementCampaignReport: { keyArgs: false, merge: true },
        marketplaceCampaignReport: { keyArgs: false, merge: true }
      }
    }
  }
});

const serverApiVersionKey = await axios(GRAPHQL_SERVER_VERSION_URL, {
  method: 'GET',
  timeout: 10_000
})
  .then((res) => res.data)
  .catch((err) => {
    console.error('version error', JSON.stringify(err));
    return '';
  });
const localStorageApiVersionKey = manageLocalStorage('get', 'apiVersionKey');

const persistor = new CachePersistor({
  cache,
  storage: new LocalStorageWrapper(window.localStorage)
});

if (serverApiVersionKey === localStorageApiVersionKey) {
  await persistor.restore();
} else {
  // If the hash keys don't match, purge the storage, write a new one, and update the hash key
  await persistor.purge();
  persistor.remove(); // this will remove the key-value pair associated with apollo cache, by default it is "apollo-cache-persist"
  manageLocalStorage('set', 'apiVersionKey', serverApiVersionKey);
}

export const client = new ApolloClient({
  cache,
  connectToDevTools: __DEV__,
  name: 'anytag-client',
  link: ApolloLink.from([namedLink, authLink, sentryLink, showError, httpLink]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      // https://github.com/apollographql/apollo-client/issues/6833
      // in short 'cache-and-network' may cause duplicate call to some APIs
      nextFetchPolicy: (lastFetchPolicy) => {
        if (lastFetchPolicy === 'cache-and-network' || lastFetchPolicy === 'network-only') {
          return 'cache-first';
        }

        return lastFetchPolicy;
      }
    },
    mutate: { errorPolicy: 'none' },
    query: { fetchPolicy: 'network-only', errorPolicy: 'all' }
  }
});

interface ApolloProviderProps {
  children: ReactNode;
}

export const ApolloProvider = ({ children }: ApolloProviderProps) => (
  <ApolloClientProvider client={client}>{children}</ApolloClientProvider>
);
